import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRealKmClient } from '../../lib/useRealKmClient.js';
import { KM_CONFIG } from '../../api/config.js';

/* ═══════════════════════════════════════════════════════════
   Esperienze in scadenza — UC-SYS-04.

   RISCRITTA il 22/09: la prima versione usava GET /hr/esperienze-in-
   scadenza come fonte della lista mostrata a video. Ma quell'endpoint
   (scan_esperienze_in_scadenza) è pensato per NOTIFICARE HR in giorni
   specifici — il confronto in v_esperienza_scadenza è un'uguaglianza
   esatta sui giorni configurati in preavvisi_giorni (es. "oggi sono
   esattamente 30 giorni prima"), non un intervallo. Usarlo come lista
   persistente dava un elenco che appariva un giorno e spariva il
   successivo — comportamento giusto per una notifica, sbagliato per una
   pagina che HR consulta quando vuole.

   Ora la pagina calcola da sé, sempre uguale a ogni apertura: prende le
   esperienze aperte (GET /esperienze, poi dettaglio per la data — con
   solo 4 esperienze nel sistema l'N+1 è trascurabile) e i giorni residui
   fino a data_fine_dichiarata (se c'è) o data_chiusura_prevista.

   27/09 — aggiunta la colonna Risorsa (se_001_consulente, dal dettaglio
   già caricato per ogni riga: nessuna chiamata in più). Cliente e
   Risorsa sono ora link al dettaglio dell'esperienza.
═══════════════════════════════════════════════════════════ */

const T = {
  border: 'rgba(0,0,0,0.10)',
  surface: '#FFFFFF',
  surface2: '#F1F0ED',
  txt2: '#6B6A65',
  txt3: '#A3A19C',
  amberBg: '#FAEEDA',
  amberTxt: '#633806',
  redBg: '#FCEBEB',
  redTxt: '#791F1F',
};

const SOGLIA_PREAVVISO_GIORNI = 30; // solo per colorare/etichettare, non filtra righe

const linkStyle = { color: 'inherit', textDecoration: 'underline', textDecorationColor: 'rgba(0,0,0,0.25)' };

export default function EsperienzeInScadenzaPage() {
  const kmClient = useRealKmClient();

  const [status, setStatus] = useState('idle');
  const [righe, setRighe] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [techDetails, setTechDetails] = useState(null);
  const [showTech, setShowTech] = useState(false);

  const load = React.useCallback(async () => {
    setStatus('loading');
    setErrorMessage(null);
    const requestUrl = '/esperienze';
    try {
      const lista = await kmClient.get(requestUrl, { query: { stato: 'aperta', limite: 200 } });
      const aperte = (lista?.esperienze || []).filter((e) => e.stato === 'aperta');
      const dettagli = await Promise.all(
        aperte.map((e) =>
          kmClient.get(`/esperienze/${encodeURIComponent(e.esperienza_id)}`)
            .then((d) => ({ ...e, ...d }))
            .catch(() => null)
        )
      );
      const arricchite = dettagli
        .filter(Boolean)
        .map((e) => {
          // 28/09: AF v2.0 UC-P03-02 — «la data di chiusura prevista è il solo
          // dato che governa i reminder di chiusura»; la fine comunicata dalla
          // risorsa resta informativa (§5.3: è HR a riportarla sulla prevista).
          const riferimento = e.data_chiusura_prevista || null;
          const giorni = riferimento ? Math.ceil((new Date(riferimento) - new Date()) / 86400000) : null;
          return { ...e, riferimento, giorni };
        })
        .filter((e) => e.riferimento)
        .sort((a, b) => a.giorni - b.giorni);
      setRighe(arricchite);
      setTechDetails({ requestUrl, status: 200, note: `${aperte.length} esperienze aperte esaminate, ${arricchite.length} con data di chiusura impostata.` });
      setStatus('ready');
    } catch (err) {
      setTechDetails({ requestUrl, status: err?.status ?? 'n/d', technicalMessage: err?.technicalMessage || null, detail: err?.detail || null });
      setErrorMessage(err?.detail || 'Errore nel caricamento delle esperienze.');
      setStatus('error');
    }
  }, [kmClient]);

  useEffect(() => {
    load();
  }, [load]);

  const scadute = righe.filter((e) => e.giorni < 0).length;
  const inPreavviso = righe.filter((e) => e.giorni >= 0 && e.giorni <= SOGLIA_PREAVVISO_GIORNI).length;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
      <Link to="/knowledge-management/esperienze" style={{ fontSize: 12.5, color: T.txt2, textDecoration: 'none', display: 'inline-block', marginBottom: 10 }}>
        ← Esperienze
      </Link>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Esperienze in scadenza</h1>
        {KM_CONFIG.HR_CONSOLE_MOCK_MODE && (
          <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10.5, fontWeight: 700, background: T.amberBg, color: T.amberTxt }}>
            DATI DI SVILUPPO — non demo
          </span>
        )}
      </div>
      <p style={{ fontSize: 12, color: T.txt2, marginBottom: 16 }}>
        Tutte le esperienze aperte, ordinate per vicinanza alla chiusura — sempre visibile, non solo nei giorni di notifica configurati (UC-SYS-04).
      </p>

      {status === 'loading' && (
        <div style={{ padding: '2rem', textAlign: 'center', color: T.txt3, background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 14 }}>
          Caricamento…
        </div>
      )}

      {status === 'error' && (
        <div style={{ padding: '1rem 1.2rem', borderRadius: 10, background: T.redBg, color: T.redTxt, fontSize: 13 }}>
          {errorMessage}
          <button type="button" onClick={load} style={{ marginLeft: 10, fontSize: 12, cursor: 'pointer' }}>Riprova</button>
        </div>
      )}

      {status === 'ready' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            {[
              ['Con data impostata', righe.length],
              ['Scadute', scadute],
              [`In preavviso (≤${SOGLIA_PREAVVISO_GIORNI}gg)`, inPreavviso],
            ].map(([label, val]) => (
              <div key={label} style={{ padding: '10px 16px', borderRadius: 10, background: T.surface, border: `0.5px solid ${T.border}`, minWidth: 100 }}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{val}</div>
                <div style={{ fontSize: 11, color: T.txt2 }}>{label}</div>
              </div>
            ))}
          </div>

          {righe.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: T.txt3, background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 14 }}>
              Nessuna esperienza aperta con una data di chiusura impostata.
            </div>
          ) : (
            <div style={{ background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.surface2 }}>
                    {['Cliente', 'Risorsa', 'Ruolo', 'Data di riferimento', 'Giorni residui'].map((h) => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.txt2, borderBottom: `0.5px solid ${T.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {righe.map((e) => {
                    const href = `/knowledge-management/esperienze/${encodeURIComponent(e.esperienza_id)}`;
                    const cliente = e.cliente || e.se_002_cliente;
                    const risorsa = e.se_001_consulente;
                    return (
                    <tr key={e.esperienza_id} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                        <Link to={href} style={linkStyle} title="Apri l'esperienza">{cliente || '—'}</Link>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {risorsa ? <Link to={href} style={linkStyle} title="Apri l'esperienza">{risorsa}</Link> : <span style={{ color: T.txt3 }}>—</span>}
                      </td>
                      <td style={{ padding: '10px 12px' }}>{e.ruolo || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>{new Date(e.riferimento).toLocaleDateString('it-IT')}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span className="badge" style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: e.giorni < 0 ? T.redBg : T.amberBg, color: e.giorni < 0 ? T.redTxt : T.amberTxt }}>
                          {e.giorni < 0 ? `Scaduta da ${Math.abs(e.giorni)} giorni` : `${e.giorni} giorni`}
                        </span>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {techDetails && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={() => setShowTech((v) => !v)}
            style={{ fontSize: 11, color: '#0C447C', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            {showTech ? '▲ Nascondi dettagli tecnici' : '▼ Mostra dettagli tecnici'}
          </button>
          {showTech && (
            <div style={{ marginTop: 6, padding: 8, borderRadius: 6, background: '#fff', border: `0.5px solid ${T.border}`, fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              URL: {techDetails.requestUrl}{'\n'}
              Status: {techDetails.status}{'\n'}
              {techDetails.technicalMessage && `Messaggio tecnico: ${techDetails.technicalMessage}\n`}
              {techDetails.detail && `Detail backend: ${techDetails.detail}\n`}
              {techDetails.note && `${techDetails.note}\n`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
